import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useEffectiveUser } from "../lib/auth";
import { canSeeBeta } from "../lib/permissions";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { Field, Textarea } from "./ui/Input";

// Purple "You're a beta tester" strip that sits at the very top of
// the layout for every account tagged `is_beta_tester = true`.
// Carries a Send feedback button that opens a modal; the modal
// posts the free-form note to the send-notification edge function,
// which emails the platform developer (jamesleonbond@gmail.com).
//
// Platform developer themselves see the banner too — handy for
// kicking the whole flow during QA — but can also opt out by
// toggling their own beta-tester flag off from Settings → Beta
// testers if the strip gets in the way.
export function BetaFeedbackBanner() {
  const me = useEffectiveUser();
  const [open, setOpen] = useState(false);
  if (!me || !canSeeBeta(me)) return null;
  return (
    <>
      <div className="shrink-0 border-b border-violet-400/60 bg-violet-600 text-white print:hidden">
        <div className="px-3 py-1.5 flex items-center justify-center gap-3 text-xs md:text-sm font-semibold tracking-wide">
          <span className="hidden sm:inline uppercase text-[10px] md:text-xs bg-white/15 border border-white/20 rounded px-1.5 py-0.5">
            Beta tester
          </span>
          <span>
            <span className="sm:hidden">Beta — share what you find.</span>
            <span className="hidden sm:inline">
              Thanks for helping test — your feedback shapes the next build.
            </span>
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ml-1 inline-flex items-center rounded-md bg-white text-violet-700 hover:bg-violet-50 font-semibold text-xs px-2 py-0.5 shadow-sm"
          >
            Send feedback
          </button>
        </div>
      </div>
      {open && (
        <BetaFeedbackModal
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function BetaFeedbackModal({ onClose }: { onClose: () => void }) {
  const me = useEffectiveUser();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      const trimmed = note.trim();
      if (!trimmed) throw new Error("Write a quick note before sending.");
      await api.sendBetaFeedback(trimmed);
    },
    onSuccess: () => {
      setSent(true);
      setError(null);
      setNote("");
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Couldn't send feedback."),
  });

  return (
    <Modal
      open={true}
      onClose={() => { if (!mut.isPending) onClose(); }}
      title="Send beta feedback"
      footer={
        sent ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => { setError(null); mut.mutate(); }}
              disabled={mut.isPending || !note.trim()}
            >
              {mut.isPending ? "Sending…" : "Send feedback"}
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
          Thanks! Your note is on its way.
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">
            What's broken, confusing, or missing? The dev reads every
            note — screenshots and reproduction steps are gold.
          </p>
          <Field label="Your feedback">
            <Textarea
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={6}
              placeholder="e.g. The coverage chip edit modal keeps the old officer highlighted after I save a swap."
            />
          </Field>
          {me && (
            <p className="text-[11px] text-slate-500">
              Sent as <span className="font-medium">{me.email}</span> — the
              dev can reply directly if more detail is needed.
            </p>
          )}
          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
